import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Dashboard from './components/Dashboard';
import SiteDetails from './pages/SiteDetails';
import AlertsHistory from './pages/AlertsHistory';
import AlertSettingsOverview from './pages/AlertSettingsOverview';
import TrendView from './pages/TrendView';
import Login from './pages/Login';
import ScrollToTop from './components/ScrollToTop';
import { SitesProvider, useSites } from './context/SitesContext';
import { WebSocketProvider, useWebSocket } from './context/WebSocketContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { fetchMetrics, fetchMetricsBySite, fetchAlerts, fetchSummary } from './services/api';

function AppContent() {
  const { sites, refreshSites } = useSites();
  const { addListener } = useWebSocket();
  const { isAuthenticated, authLoading } = useAuth();
  const [metrics, setMetrics] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = async (includeSites = false) => {
    try {
      setLoading(true);
      // Only refresh sites if explicitly requested (via refresh button)
      if (includeSites) {
        await refreshSites();
      }
      
      // Always refresh metrics, alerts, and summary
      const [metricsData, alertsData, summaryData] = await Promise.all([
        fetchMetrics(),
        fetchAlerts(),
        fetchSummary()
      ]);
      
      setMetrics(metricsData);
      setAlerts(alertsData);
      setSummary(summaryData);
      setError(null);
    } catch (err) {
      setError('Failed to load monitoring data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const refreshAlerts = async () => {
    try {
      const alertsData = await fetchAlerts();
      setAlerts(alertsData);
    } catch (err) {
      console.error('Failed to refresh alerts:', err);
      // Fallback to full refresh
      await loadData();
    }
  };

  useEffect(() => {
    // Load metrics, alerts, and summary on startup (sites already loaded by context)
    loadData(false);
    
    // Listen to WebSocket events from shared connection
    const removeListener = addListener((data) => {
      if (data.type === 'metrics_updated') {
        // Immediately update metrics with WebSocket data for instant UI update
        if (data.siteId && data.metrics) {
          // Update metrics immediately with WebSocket data (instant UI update)
          setMetrics(prevMetrics => {
            const updatedMetrics = prevMetrics.filter(m => m.siteId !== data.siteId);
            // Use WebSocket data directly - includes heartbeat status and fresh lastUpdated
            const newMetrics = {
              ...data.metrics,
              siteId: data.siteId
            };
            updatedMetrics.push(newMetrics);
            return updatedMetrics;
          });
          
          // Then fetch full metrics with status calculation in the background (for accuracy)
          // Use a small delay to avoid race conditions
          setTimeout(() => {
            fetchMetricsBySite(data.siteId).then(siteMetrics => {
              setMetrics(prevMetrics => {
                // Update with server-calculated status
                const updatedMetrics = prevMetrics.filter(m => m.siteId !== data.siteId);
                updatedMetrics.push(siteMetrics);
                return updatedMetrics;
              });
            }).catch(err => {
              console.error('Error fetching updated metrics for site:', err);
              // Don't fallback to full refresh - we already have WebSocket data
            });
          }, 100);
          
          // Also refresh alerts in the background (non-blocking)
          // Summary will be updated via WebSocket if server sends summary_updated event
          fetchAlerts().then(alertsData => {
            setAlerts(alertsData);
          }).catch(err => {
            console.error('Error refreshing alerts after WebSocket update:', err);
          });
        } else {
          // Fallback: if no siteId or metrics, do full refresh
          loadData(false);
        }
      } else if (data.type === 'summary_updated') {
        // Update summary immediately when received via WebSocket
        if (data.summary) {
          setSummary(data.summary);
        }
      } else if (data.type === 'polling_refresh') {
        // For polling refresh, do full data load
        loadData(false);
      }
    });
    
    return () => {
      removeListener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addListener]);

  // Don't render routes until auth check is complete to preserve current route
  if (authLoading) {
    return (
      <Router>
        <ScrollToTop />
        <div className="app-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </Router>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      {!isAuthenticated() ? (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      ) : (
        <div className="App">
          <Routes>
            <Route 
              path="/" 
              element={
                loading && !summary ? (
                  <div className="app-loading">
                    <div className="spinner"></div>
                    <p>Loading monitoring data...</p>
                  </div>
                ) : (
                  <Dashboard
                    sites={sites}
                    metrics={metrics}
                    alerts={alerts}
                    summary={summary}
                    error={error}
                    onRefresh={() => loadData(true)}
                    onAlertsUpdate={refreshAlerts}
                  />
                )
              } 
            />
            <Route path="/site/:siteId" element={<SiteDetails />} />
            <Route path="/trends/:siteId/:metricType?" element={<TrendView />} />
            <Route path="/alerts-history" element={<AlertsHistory />} />
            <Route path="/alert-settings" element={<AlertSettingsOverview />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      )}
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <SitesProvider>
        <WebSocketProvider>
          <AppContent />
        </WebSocketProvider>
      </SitesProvider>
    </AuthProvider>
  );
}

export default App;

